import React from 'react';
import { ArrowLeftRightIcon } from 'lucide-react';
import { DuplicateSet, CustomerRecord } from './DuplicateCard';
import { RecordCard } from './RecordCard';
import { FieldComparison } from './FieldComparison';
import { ConfidenceBadge } from './ConfidenceBadge';
type FieldSelection = {
  [fieldName: string]: 'left' | 'right' | null;
};
type ComparisonViewProps = {
  duplicateSet: DuplicateSet;
  fieldSelections: FieldSelection;
  onFieldSelect: (fieldName: string, side: 'left' | 'right') => void;
};
type FieldConfig = {
  name: keyof CustomerRecord;
  label: string;
  format?: (value: string) => string;
  suggestNewest?: boolean;
  suggestHigher?: boolean;
};
const fieldConfigs: FieldConfig[] = [{
  name: 'firstName',
  label: 'First Name'
}, {
  name: 'lastName',
  label: 'Last Name'
}, {
  name: 'email',
  label: 'Email Address'
}, {
  name: 'phone',
  label: 'Phone Number'
}, {
  name: 'address',
  label: 'Address'
}, {
  name: 'loanAmount',
  label: 'Loan Amount',
  format: v => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
  }).format(Number(v)),
  suggestHigher: true
}, {
  name: 'applicationDate',
  label: 'Application Date',
  format: v => new Date(v).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }),
  suggestNewest: true
}, {
  name: 'ssn',
  label: 'SSN (Masked)',
  format: v => v
}];
export function ComparisonView({
  duplicateSet,
  fieldSelections,
  onFieldSelect
}: ComparisonViewProps) {
  const [leftRecord, rightRecord] = duplicateSet.records;
  const getSuggestedSide = (config: FieldConfig): 'left' | 'right' | undefined => {
    const leftVal = String(leftRecord[config.name]);
    const rightVal = String(rightRecord[config.name]);
    if (leftVal === rightVal) return undefined;
    if (config.suggestNewest) {
      const leftDate = new Date(leftVal);
      const rightDate = new Date(rightVal);
      return leftDate > rightDate ? 'left' : 'right';
    }
    if (config.suggestHigher) {
      return Number(leftVal) > Number(rightVal) ? 'left' : 'right';
    }
    // Default: suggest the one from newer record
    const leftCreated = new Date(leftRecord.createdAt);
    const rightCreated = new Date(rightRecord.createdAt);
    return leftCreated > rightCreated ? 'left' : 'right';
  };
  return <div className="flex-1 overflow-y-auto" data-id="element-1">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10" data-id="element-2">
        <div className="flex items-center justify-between" data-id="element-3">
          <div className="flex items-center gap-4" data-id="element-4">
            <ArrowLeftRightIcon className="w-6 h-6 text-blue-500" aria-hidden="true" data-id="element-5" />
            <div data-id="element-6">
              <h2 className="text-xl font-semibold text-gray-900" data-id="element-7">
                Compare & Merge Records
              </h2>
              <p className="text-sm text-gray-600" data-id="element-8">
                Click on the value you want to keep for each field
              </p>
            </div>
          </div>
          <ConfidenceBadge score={duplicateSet.confidenceScore} size="lg" data-id="element-9" />
        </div>
      </div>

      {/* Record Cards Overview */}
      <div className="p-6" data-id="element-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-11">
          <RecordCard record={leftRecord} side="left" data-id="element-12" />
          <RecordCard record={rightRecord} side="right" data-id="element-13" />
        </div>

        {/* Field-by-Field Comparison */}
        <div className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-14">
          <h3 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-15">
            Field-by-Field Selection
          </h3>
          <p className="text-sm text-gray-600 mb-6" data-id="element-16">
            <span className="inline-flex items-center gap-1" data-id="element-17">
              <span className="w-3 h-3 rounded-full bg-green-500" aria-hidden="true" data-id="element-18"></span>
              Green = Matching values
            </span>
            <span className="mx-3" data-id="element-19">|</span>
            <span className="inline-flex items-center gap-1" data-id="element-20">
              <span className="w-3 h-3 rounded-full bg-yellow-500" aria-hidden="true" data-id="element-21"></span>
              Yellow = Conflicting values (select one)
            </span>
            <span className="mx-3" data-id="element-22">|</span>
            <span className="inline-flex items-center gap-1" data-id="element-23">
              <span className="w-3 h-3 rounded-full bg-blue-500" aria-hidden="true" data-id="element-24"></span>
              Star = AI recommended
            </span>
          </p>

          <div className="space-y-6" data-id="element-25">
            {fieldConfigs.map(config => <FieldComparison key={config.name} fieldName={config.name} fieldLabel={config.label} leftValue={String(leftRecord[config.name])} rightValue={String(rightRecord[config.name])} selectedSide={fieldSelections[config.name]} suggestedSide={getSuggestedSide(config)} onSelect={side => onFieldSelect(config.name, side)} formatValue={config.format} data-id="element-26" />)}
          </div>
        </div>
      </div>
    </div>;
}